import { useRef } from 'react';

import useAuth from '@fhs-legacy/frontend/src/hooks/auth/use-auth';
import useDebounceEffect from '@fhs-legacy/frontend/src/hooks/use-debounce-effect';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import { IAppliedRewards } from '@fhs-legacy/frontend/src/state/loyalty/hooks/types';
import { useServiceModeContext } from '@fhs-legacy/frontend/src/state/service-mode';
import { useStoreContext } from '@fhs-legacy/frontend/src/state/store';

import { usePaymentFlowStore } from '../payment';

export function useCartPriceOrder() {
  /* initializes the price order call and the getOrder polling, can be available on cart and checkout as well as other pages, currently only cart;
  has an interval check where it will price after 15 minutes also found in cart context
  */
  const appliedLoyaltyRewards: IAppliedRewards = useAppSelector(
    selectors.loyalty.selectAppliedLoyaltyRewards
  );
  const isRewardApplied = Object.keys(appliedLoyaltyRewards).length;

  const selectedOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const incentiveApplied = isRewardApplied || selectedOffer;

  const {
    cartEntries,
    tryPricing,
    serverOrder,
    serverOrderLoading,
    shouldSkipPriceOrder,
    setShouldSkipPriceOrder,
  } = useCartContext();

  const currentState = usePaymentFlowStore(state => state.currentState);
  const { serviceMode } = useServiceModeContext();

  const { isStoreAvailable } = useStoreContext();
  const { user } = useAuth();
  const rewardChanged = user?.details?.loyaltyTier;

  const lastPricingTrigger = useRef<number>(0);

  const handleRestoreCartErrorPricing = () =>
    shouldSkipPriceOrder && setShouldSkipPriceOrder(false);

  useDebounceEffect(
    () => {
      if (!isStoreAvailable || !serviceMode) {
        return;
      }

      const now = Date.now();
      if (now - lastPricingTrigger.current < 500) {
        return;
      }
      lastPricingTrigger.current = now;

      handleRestoreCartErrorPricing();
      // do not price while committing order to avoid price_requested and price_inserted infinite loop
      currentState !== 'committing' && tryPricing();
    },
    250,
    [cartEntries, isStoreAvailable, serviceMode, rewardChanged]
  );

  return {
    serverOrder,
    serverOrderLoading,
    serverOrderTotal: serverOrder?.cart?.subTotalCents,
    incentiveApplied,
  };
}
